<script>
import { toast } from "@zerodevx/svelte-toast";
import { fade, fly } from "svelte/transition";
import { flip } from "svelte/animate";
import { cubicOut } from "svelte/easing";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import * as m from "@app/paraglide/messages.js";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    displayName,
    initials,
    errorMessage,
    motion,
    saveGroup,
    removeGroup,
    addMemberToGroup,
    removeMemberFromGroup,
} from "./teamApi.js";

/** @type {{ teamId: string, users: any[], groups: any[], onchange: () => Promise<void> }} */
let { teamId, users, groups, onchange } = $props();

let search = $state(""),
    draft = $state(null),
    memberSearch = $state(""),
    saving = $state(false),
    pending = $state({});

/* Membership comes from each user's `groups` (what access checks read); group.members is a mirror. */
function membersOf(groupId) {
    return users.filter((u) => (u.groups || []).includes(groupId));
}

const filtered = $derived.by(() => {
    const needle = search.trim().toLowerCase();
    return needle ? groups.filter((g) => (g.name || "").toLowerCase().includes(needle)) : groups;
});

const memberChoices = $derived.by(() => {
    const needle = memberSearch.trim().toLowerCase();
    return users.filter(
        (u) => !needle || displayName(u).toLowerCase().includes(needle) || (u.email || "").toLowerCase().includes(needle),
    );
});

function openGroup(group) {
    draft = { id: group.id, name: group.name || "", description: group.description || "", isNew: false };
    memberSearch = "";
}

function openNewGroup() {
    draft = { id: uuidv4(), name: "", description: "", isNew: true };
    memberSearch = "";
}

async function save() {
    const name = draft.name.trim();
    if (!name) {
        toast.push(m.team_manager_group_name_required(), { classes: ["toast-danger"] });
        return;
    }
    if (groups.some((g) => g.id !== draft.id && (g.name || "").trim().toLowerCase() === name.toLowerCase())) {
        toast.push(m.team_manager_group_name_exists(), { classes: ["toast-danger"] });
        return;
    }

    saving = true;
    try {
        await saveGroup(teamId, draft, membersOf(draft.id).map((u) => u.id));
        toast.push(m.team_manager_group_saved_success(), { classes: ["toast-success"] });
        // A new group stays open so members can be added straight away.
        draft = { ...draft, name, isNew: false };
        await onchange();
    } catch (err) {
        console.error(err);
        toast.push(m.team_manager_save_group_failed({ error: errorMessage(err) }), { classes: ["toast-danger"] });
    } finally {
        saving = false;
    }
}

async function remove() {
    const group = draft;
    const confirm = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        icon: "warning",
        title: m.team_manager_remove_group_confirm_title({ name: group.name || m.team_manager_unnamed_group() }),
        text: m.team_manager_remove_group_confirm_text(),
        showCancelButton: true,
        confirmButtonText: m.team_manager_delete_group_button(),
        cancelButtonText: m.cancel(),
        focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    saving = true;
    try {
        await removeGroup(teamId, group);
        toast.push(m.team_manager_group_removed_success(), { classes: ["toast-success"] });
        draft = null;
        await onchange();
    } catch (err) {
        console.error(err);
        toast.push(m.team_manager_remove_group_failed({ error: errorMessage(err) }), { classes: ["toast-danger"] });
    } finally {
        saving = false;
    }
}

async function toggleMember(user, inGroup) {
    pending[user.id] = true;
    try {
        if (inGroup) {
            await removeMemberFromGroup(teamId, user.id, draft.id);
        } else {
            await addMemberToGroup(teamId, user.id, draft.id);
        }
        await onchange();
    } catch (err) {
        console.error(err);
        const message = inGroup ? m.team_manager_remove_user_from_group_failed : m.team_manager_add_user_to_group_failed;
        toast.push(message({ error: errorMessage(err) }), { classes: ["toast-danger"] });
    } finally {
        pending[user.id] = false;
    }
}

function rowKeydown(e, group) {
    if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openGroup(group);
    }
}

function panelKeydown(e) {
    if (e.key === "Escape") {
        e.stopPropagation();
        draft = null;
    }
}
</script>

<div class="team-toolbar">
    <div class="input-group input-group-sm team-search">
        <span class="input-group-text"><i class="bi bi-search" aria-hidden="true"></i></span>
        <input
            type="search"
            class="form-control"
            placeholder={m.team_manager_search_groups_placeholder()}
            aria-label={m.team_manager_search_groups_placeholder()}
            bind:value={search} />
    </div>
    <button type="button" class="btn btn-primary btn-sm ms-auto" onclick={openNewGroup}>
        <i class="bi bi-plus-lg me-1"></i>{m.team_manager_new_group_button()}
    </button>
</div>

<div class="team-layout">
    <div class="team-table-wrap">
        {#if groups.length === 0}
            <div class="team-empty">
                <i class="bi bi-diagram-3" aria-hidden="true"></i>
                <p class="fw-semibold mb-1">{m.team_manager_no_groups_title()}</p>
                <p class="small text-body-secondary mb-3">{m.team_manager_no_groups_text()}</p>
                <button type="button" class="btn btn-primary btn-sm" onclick={openNewGroup}>{m.team_manager_new_group_button()}</button>
            </div>
        {:else if filtered.length === 0}
            <div class="team-empty">
                <i class="bi bi-search" aria-hidden="true"></i>
                <p class="small text-body-secondary mb-0">{m.team_manager_no_matches()}</p>
            </div>
        {:else}
            <table class="table table-hover align-middle mb-0 team-table">
                <thead>
                    <tr>
                        <th scope="col">{m.team_manager_group_name_label()}</th>
                        <th scope="col" class="d-none d-lg-table-cell">{m.team_manager_group_description_label()}</th>
                        <th scope="col">{m.team_manager_table_members_count()}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each filtered as group (group.id)}
                        {@const members = membersOf(group.id)}
                        <tr
                            animate:flip={{ duration: motion(200) }}
                            in:fade={{ duration: motion(160) }}
                            class={draft?.id === group.id ? "table-active" : ""}
                            tabindex="0"
                            aria-label={group.name || m.team_manager_unnamed_group()}
                            onclick={() => openGroup(group)}
                            onkeydown={(e) => rowKeydown(e, group)}>
                            <td class="fw-semibold"><i class="bi bi-diagram-3 me-2 text-body-secondary"></i>{group.name || m.team_manager_unnamed_group()}</td>
                            <td class="d-none d-lg-table-cell small text-body-secondary text-truncate group-desc">{group.description || "—"}</td>
                            <td>
                                <div class="avatar-stack" title={members.map(displayName).join(", ")}>
                                    {#each members.slice(0, 4) as user (user.id)}
                                        <span class="team-avatar team-avatar--sm" aria-hidden="true">{initials(user)}</span>
                                    {/each}
                                    <span class="small text-body-secondary ms-2">{members.length}</span>
                                </div>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/if}
    </div>

    {#if draft}
        <div class="panel-slot" transition:fly={{ x: 40, duration: motion(240), easing: cubicOut }}>
        {#key draft.id}
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <aside class="team-panel-shell" aria-labelledby="groupPanelTitle" onkeydown={panelKeydown} in:fade={{ duration: motion(160) }}>
            <div class="team-panel-shell__header">
                <span class="team-avatar team-avatar--lg" aria-hidden="true"><i class="bi bi-diagram-3"></i></span>
                <h5 class="mb-0 text-truncate" id="groupPanelTitle">
                    {draft.isNew ? m.team_manager_add_group_title() : draft.name || m.team_manager_unnamed_group()}
                </h5>
                <button type="button" class="btn-close ms-auto" aria-label={m.close()} onclick={() => (draft = null)}></button>
            </div>
            <div class="team-panel-shell__body">
                <form onsubmit={(e) => { e.preventDefault(); save(); }}>
                    <div class="mb-3">
                        <label class="form-label small fw-semibold" for="groupName">{m.team_manager_group_name_label()}</label>
                        <input id="groupName" class="form-control form-control-sm" bind:value={draft.name} required autocomplete="off" />
                    </div>
                    <div class="mb-3">
                        <label class="form-label small fw-semibold" for="groupDescription">{m.team_manager_group_description_label()}</label>
                        <textarea id="groupDescription" class="form-control form-control-sm" rows="2" bind:value={draft.description}></textarea>
                    </div>
                    <div class="d-flex gap-2 mb-4">
                        <button type="submit" class="btn btn-primary btn-sm flex-grow-1" disabled={saving}>
                            {#if saving}<span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>{/if}
                            {m.team_manager_save_group_button()}
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => (draft = null)} disabled={saving}>{m.cancel()}</button>
                    </div>
                </form>

                <div class="form-label small fw-semibold mb-1">{m.team_manager_users_label()}</div>
                {#if draft.isNew}
                    <div class="small text-body-secondary">{m.team_manager_save_group_before_users()}</div>
                {:else}
                    <input
                        type="search"
                        class="form-control form-control-sm mb-2"
                        placeholder={m.team_manager_search_members_placeholder()}
                        aria-label={m.team_manager_search_members_placeholder()}
                        bind:value={memberSearch} />
                    <ul class="list-unstyled member-picker mb-0">
                        {#each memberChoices as user (user.id)}
                            {@const inGroup = (user.groups || []).includes(draft.id)}
                            <li>
                                <label class="member-pick {inGroup ? 'active' : ''}">
                                    <input
                                        type="checkbox"
                                        class="form-check-input mt-0"
                                        checked={inGroup}
                                        disabled={pending[user.id]}
                                        onchange={() => toggleMember(user, inGroup)} />
                                    <span class="team-avatar team-avatar--sm" aria-hidden="true">{initials(user)}</span>
                                    <span class="min-w-0">
                                        <span class="d-block text-truncate">{displayName(user)}</span>
                                        {#if user.firstName || user.lastName}<span class="d-block small text-body-secondary text-truncate">{user.email}</span>{/if}
                                    </span>
                                    {#if pending[user.id]}<span class="spinner-border spinner-border-sm ms-auto" aria-hidden="true"></span>{/if}
                                </label>
                            </li>
                        {:else}
                            <li class="small text-body-secondary">{m.team_manager_no_users_available()}</li>
                        {/each}
                    </ul>

                    <div class="danger-zone mt-4">
                        <div class="small fw-semibold text-danger mb-1">{m.team_manager_danger_zone()}</div>
                        <button type="button" class="btn btn-outline-danger btn-sm w-100" onclick={remove} disabled={saving}>
                            <i class="bi bi-trash me-1"></i>{m.team_manager_delete_group_button()}
                        </button>
                    </div>
                {/if}
            </div>
        </aside>
        {/key}
        </div>
    {/if}
</div>

<style>
    .min-w-0 {
        min-width: 0;
    }

    .team-search {
        max-width: 18rem;
    }

    .group-desc {
        max-width: 20rem;
    }

    .avatar-stack {
        display: flex;
        align-items: center;
    }

    .avatar-stack .team-avatar + .team-avatar {
        margin-left: -0.4rem;
    }

    .team-panel-shell {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--bs-border-color);
        border-radius: 0.5rem;
        background: var(--bs-body-bg);
        min-height: 0;
        overflow: hidden;
    }

    .team-panel-shell__header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 1rem;
        border-bottom: 1px solid var(--bs-border-color);
        background: var(--bs-tertiary-bg);
    }

    .team-panel-shell__body {
        padding: 1rem;
        overflow-y: auto;
    }

    .member-picker {
        max-height: 16rem;
        overflow-y: auto;
        border: 1px solid var(--bs-border-color);
        border-radius: 0.375rem;
    }

    .member-pick {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.4rem 0.6rem;
        cursor: pointer;
        transition: background-color 0.15s ease-in-out;
    }

    .member-picker li + li .member-pick {
        border-top: 1px solid var(--bs-border-color-translucent);
    }

    .member-pick:hover {
        background: var(--bs-tertiary-bg);
    }

    .member-pick.active {
        background: var(--bs-primary-bg-subtle);
    }

    .danger-zone {
        padding-top: 1rem;
        border-top: 1px dashed var(--bs-border-color);
    }
</style>
